import React, { useState, useEffect } from "react"
import { Head } from "@inertiajs/react"
import { router } from "@inertiajs/react"
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Edit, Trash2, Eye, Mail, Copy, X, MoreVertical } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import VariableInsertButton from "@/components/shared/VariableInsertButton"
import EmailRichTextEditor from "@/components/shared/EmailRichTextEditor"
import TemplateVariablesTab from "@/components/shared/TemplateVariablesTab"
import {
    splitCustomVariables,
    mergeTemplateVariables,
    buildInsertVariableList,
    getEmailVariableCatalog,
    parseEmailVariablesFromContent,
    buildSampleDataFromVariables,
} from "@/lib/templateVariableHelpers"
import { formatPlainTextAsHtmlEmail } from "@/lib/textTemplateVariables"
import { prepareEmailHtmlForDisplay } from "@/lib/emailHtmlNormalize"
import {
    TEMPLATE_MODAL_OVERLAY_CLASS,
    EMAIL_TEMPLATE_EDITOR_DIALOG_CLASS,
    EMAIL_TEMPLATE_PREVIEW_DIALOG_CLASS,
    TEMPLATE_MODAL_TABS_LIST_CLASS,
    TEMPLATE_MODAL_TABS_TRIGGER_CLASS,
} from "@/lib/templateModalStyles"

export default function EmailTemplates({ templates = [] }) {
    const { toast } = useToast()

    // Load saved filters from localStorage on component mount
    const loadSavedFilters = () => {
        try {
            const savedFilters = localStorage.getItem('email-templates-filters');
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                return {
                    searchTerm: filters.searchTerm || "",
                    statusFilter: filters.statusFilter || "All Statuses",
                };
            }
        } catch (error) {
            console.error('Error loading saved filters:', error);
        }
        
        // Return default values if no saved filters or error
        return {
            searchTerm: "",
            statusFilter: "All Statuses",
        };
    };

    // Save filters to localStorage
    const saveFilters = (filters) => {
        try {
            localStorage.setItem('email-templates-filters', JSON.stringify(filters));
        } catch (error) {
            console.error('Error saving filters:', error);
        }
    };

    // Load initial filter values
    const initialFilters = loadSavedFilters();

    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [previewContent, setPreviewContent] = useState(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm)
    const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter)
    const [isDuplicating, setIsDuplicating] = useState(false)
    const [livePreviewContent, setLivePreviewContent] = useState(null)
    const [customVariables, setCustomVariables] = useState([])
    const [isSavingVariables, setIsSavingVariables] = useState(false)

    const variableCatalog = getEmailVariableCatalog()
    
    const [formData, setFormData] = useState({
        name: "",
        key: "",
        subject: "",
        description: "",
        content: "",
        variables: [],
        email_account_id: "none",
        is_active: true,
    })

    // Available template variables with descriptions
    const insertVariables = React.useMemo(
        () => buildInsertVariableList(variableCatalog, customVariables),
        [variableCatalog, customVariables],
    )

    const isEditing = !!selectedTemplate?.id

    const filteredTemplates = templates.filter(template => {
        const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.subject.toLowerCase().includes(searchTerm.toLowerCase())
        
        const matchesStatus = statusFilter === "All Statuses" || 
            (statusFilter === "Active" && template.is_active) ||
            (statusFilter === "Inactive" && !template.is_active)
        
        return matchesSearch && matchesStatus
    })

    const resetForm = () => {
        setFormData({
            name: "",
            key: "",
            subject: "",
            description: "",
            content: "",
            variables: [],
            email_account_id: "none",
            is_active: true,
        })
        setCustomVariables([])
        setSelectedTemplate(null)
        setIsDuplicating(false)
        setLivePreviewContent(null)
    }

    const openModal = (template = null, duplicate = false) => {
        const catalogKeys = variableCatalog.map((v) => v.key)
        if (template) {
            setSelectedTemplate(duplicate ? null : template)
            setIsDuplicating(duplicate)
            setCustomVariables(splitCustomVariables(template.variables || [], catalogKeys))
            setFormData({
                name: duplicate ? `${template.name} (Copy)` : template.name,
                key: duplicate ? `${template.key}-copy` : template.key,
                subject: template.subject,
                description: template.description || "",
                content: template.content || "",
                variables: template.variables || [],
                email_account_id: template.email_account_id || "none",
                is_active: template.is_active,
            })
        } else {
            resetForm()
        }
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setTimeout(resetForm, 300)
    }

    const buildSubmitPayload = () => ({
        ...formData,
        variables: mergeTemplateVariables(
            customVariables,
            parseEmailVariablesFromContent(formData.content, formData.subject),
        ),
    })

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsSubmitting(true)

        const payload = buildSubmitPayload()

        try {
            if (isEditing && !isDuplicating) {
                await router.put(`/email-templates/${selectedTemplate.id}`, payload, {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast({
                            title: "Success",
                            description: "Email template updated successfully.",
                        })
                        closeModal()
                    },
                    onError: (errors) => {
                        console.error('Update errors:', errors)
                        const errorMessage = errors.message || Object.values(errors).flat().join(', ') || "Failed to update email template."
                        toast({
                            title: "Error",
                            description: errorMessage,
                            variant: "destructive",
                        })
                    }
                })
            } else {
                await router.post('/email-templates', payload, {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast({
                            title: "Success",
                            description: `Email template ${isDuplicating ? 'duplicated' : 'created'} successfully.`,
                        })
                        closeModal()
                    },
                    onError: (errors) => {
                        console.error('Create errors:', errors)
                        const errorMessage = errors.message || Object.values(errors).flat().join(', ') || `Failed to ${isDuplicating ? 'duplicate' : 'create'} email template.`
                        toast({
                            title: "Error",
                            description: errorMessage,
                            variant: "destructive",
                        })
                    }
                })
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "An unexpected error occurred.",
                variant: "destructive",
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleDelete = async (template) => {
        if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
            return
        }

        try {
            await router.delete(`/email-templates/${template.id}`, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    toast({
                        title: "Success",
                        description: "Email template deleted successfully.",
                    })
                },
                onError: () => {
                    toast({
                        title: "Error",
                        description: "Failed to delete email template.",
                        variant: "destructive",
                    })
                }
            })
        } catch (error) {
            toast({
                title: "Error",
                description: "An unexpected error occurred.",
                variant: "destructive",
            })
        }
    }

    const handlePreview = async (template) => {
        try {
            const response = await fetch(`/email-templates/${template.id}/preview`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                },
                body: JSON.stringify({})
            })
            
            const data = await response.json()
            setPreviewContent({
                ...data,
                content: prepareEmailHtmlForDisplay(data.content),
            })
            setIsPreviewOpen(true)
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to generate preview.",
                variant: "destructive",
            })
        }
    }

    const syncFromSavedTemplate = (template) => {
        const catalogKeys = variableCatalog.map((v) => v.key)
        setCustomVariables(splitCustomVariables(template.variables || [], catalogKeys))
        setFormData((prev) => ({ ...prev, variables: template.variables || [] }))
        if (!isDuplicating) {
            setSelectedTemplate(template)
        }
    }

    const buildVariablesPayload = (nextCustomVariables) => ({
        ...formData,
        variables: mergeTemplateVariables(
            nextCustomVariables,
            parseEmailVariablesFromContent(formData.content, formData.subject),
        ),
    })

    const persistCustomVariables = async (nextCustomVariables, successMessage) => {
        const previousCustomVariables = customVariables
        setCustomVariables(nextCustomVariables)

        const templateId = selectedTemplate?.id
        if (!templateId || isDuplicating) {
            return
        }

        setIsSavingVariables(true)

        try {
            await new Promise((resolve, reject) => {
                router.put(`/email-templates/${templateId}`, buildVariablesPayload(nextCustomVariables), {
                    preserveState: true,
                    preserveScroll: true,
                    only: ['templates'],
                    onSuccess: (page) => {
                        const updated = page.props.templates?.find((t) => t.id === templateId)
                        if (updated) {
                            syncFromSavedTemplate(updated)
                        }
                        toast({
                            title: "Success",
                            description: successMessage,
                        })
                        resolve(updated)
                    },
                    onError: (errors) => {
                        setCustomVariables(previousCustomVariables)
                        toast({
                            title: "Error",
                            description: errors.message || Object.values(errors).flat().join(', ') || "Failed to save custom variable.",
                            variant: "destructive",
                        })
                        reject(errors)
                    },
                })
            })
        } finally {
            setIsSavingVariables(false)
        }
    }

    const addCustomVariable = async (variable) => {
        await persistCustomVariables(
            [...customVariables, variable],
            "Custom variable added.",
        )
    }

    const removeCustomVariable = async (variableKey) => {
        await persistCustomVariables(
            customVariables.filter((v) => v.key !== variableKey),
            "Custom variable removed.",
        )
    }

    const insertVariableIntoField = (variableText, fieldId = 'content') => {
        const field = document.getElementById(fieldId)
        if (field) {
            const start = field.selectionStart
            const end = field.selectionEnd
            const value = fieldId === 'subject' ? formData.subject : formData.content
            const newValue = value.substring(0, start) + variableText + value.substring(end)
            setFormData({
                ...formData,
                [fieldId === 'subject' ? 'subject' : 'content']: newValue,
            })

            setTimeout(() => {
                field.focus()
                field.setSelectionRange(start + variableText.length, start + variableText.length)
            }, 0)
        }
    }

    const insertVariableIntoSubject = (variableText) => insertVariableIntoField(variableText, 'subject')

    const generateLivePreview = () => {
        if (!formData.content) {
            setLivePreviewContent(null)
            return
        }

        const sampleData = {
            'user->name': 'John Doe',
            'user->email': 'john.doe@example.com',
            'user->firstName': 'John',
            'user->lastName': 'Doe',
            'key': 'sample-key-123',
            'url': window.location.origin,
            'app_name': 'DME Platform',
            'date': new Date().getFullYear().toString(),
            ...buildSampleDataFromVariables(customVariables),
        }

        let previewContent = formData.content
        let previewSubject = formData.subject
        
        Object.entries(sampleData).forEach(([key, value]) => {
            const regex = new RegExp(`\\{\\{\\s*\\$${key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}\\s*\\}\\}`, 'g')
            previewContent = previewContent.replace(regex, value)
            previewSubject = previewSubject.replace(regex, value)
        })

        setLivePreviewContent({
            subject: previewSubject,
            content: prepareEmailHtmlForDisplay(previewContent),
        })
    }

    // Generate live preview when content or subject changes
    useEffect(() => {
        generateLivePreview()
    }, [formData.content, formData.subject, customVariables])

    // Handle ?action=new URL parameter to open create form from quick actions menu
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('action') === 'new') {
            openModal();
            // Clean up URL
            router.replace(window.location.pathname, { preserveState: true, preserveScroll: true });
        }
    }, [])

    // Save filters to localStorage whenever they change
    useEffect(() => {
        const filtersToSave = {
            searchTerm,
            statusFilter,
        };
        saveFilters(filtersToSave);
    }, [searchTerm, statusFilter]);

    // Reset all filters
    const resetFilters = () => {
        setSearchTerm("");
        setStatusFilter("All Statuses");
    };

    // Count active filters
    const getActiveFilterCount = () => {
        let count = 0;
        if (searchTerm) count++;
        if (statusFilter !== "All Statuses") count++;
        return count;
    };

    return (
        <AuthenticatedLayout>
            <Head title="DME | Email Templates" />
            
            <div className="flex flex-col gap-6 pb-8 pt-6 px-4 md:px-6 w-full">
                <div className="flex flex-col gap-2">
                    <h1 className="text-3xl font-bold tracking-tight">Email Templates</h1>
                    <p className="text-muted-foreground">
                        Manage your email templates for user communications
                    </p>
                </div>

                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex gap-4 items-center">
                        <Input
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="max-w-sm"
                        />
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All Statuses">All Statuses</SelectItem>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="Inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                        {getActiveFilterCount() > 0 && (
                            <Button variant="ghost" size="sm" onClick={resetFilters}>
                                <X className="mr-2 h-4 w-4" />
                                Reset
                            </Button>
                        )}
                    </div>
                </div>

                <div className="text-sm text-muted-foreground">
                    {filteredTemplates.length} template{filteredTemplates.length !== 1 ? "s" : ""} found
                </div>

                <Card>
                    <CardContent className="p-0">
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[80px]">ID</TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead className="hidden md:table-cell">Key</TableHead>
                                        <TableHead className="hidden lg:table-cell">Subject</TableHead>
                                        <TableHead className="hidden lg:table-cell">Variables</TableHead>
                                        <TableHead className="hidden xl:table-cell">Status</TableHead>
                                        <TableHead className="hidden xl:table-cell">Updated</TableHead>
                                        <TableHead className="w-[50px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredTemplates.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} className="h-24 text-center">
                                                <div className="flex flex-col items-center gap-2">
                                                    <Mail className="h-8 w-8 text-muted-foreground" />
                                                    <p className="text-muted-foreground">
                                                        {searchTerm || statusFilter !== "All Statuses" ? "No templates match your filters." : "No email templates found."}
                                                    </p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredTemplates.map((template) => (
                                            <TableRow
                                                key={template.id}
                                                className="cursor-pointer hover:bg-muted/50"
                                                onClick={() => openModal(template)}
                                            >
                                                <TableCell>{template.id}</TableCell>
                                                <TableCell>
                                                    <div>
                                                        <div className="font-medium">{template.name}</div>
                                                        <div className="text-sm text-muted-foreground md:hidden">
                                                            {template.key}
                                                        </div>
                                                        {template.description && (
                                                            <div className="text-sm text-muted-foreground">
                                                                {template.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    <code className="text-sm bg-muted px-2 py-1 rounded">
                                                        {template.key}
                                                    </code>
                                                </TableCell>
                                                <TableCell className="hidden lg:table-cell">
                                                    <div className="max-w-[200px] truncate" title={template.subject}>
                                                        {template.subject}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden lg:table-cell">
                                                    <Badge variant="outline">
                                                        {template.variables?.length || 0} vars
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="hidden xl:table-cell">
                                                    <Badge variant="outline" className={template.is_active ? "bg-green-100 text-green-800" : "text-muted-foreground"}>
                                                        {template.is_active ? "Active" : "Inactive"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="hidden xl:table-cell">
                                                    {template.updated_at}
                                                </TableCell>
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" className="h-8 w-8 p-0">
                                                                <span className="sr-only">Open menu</span>
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                                            <DropdownMenuItem onClick={() => openModal(template)}>
                                                                <Edit className="mr-2 h-4 w-4" />
                                                                Edit Template
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => openModal(template, true)}>
                                                                <Copy className="mr-2 h-4 w-4" />
                                                                Duplicate
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => handlePreview(template)}>
                                                                <Eye className="mr-2 h-4 w-4" />
                                                                Preview
                                                            </DropdownMenuItem>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem
                                                                className="text-red-600"
                                                                onClick={() => handleDelete(template)}
                                                            >
                                                                <Trash2 className="mr-2 h-4 w-4" />
                                                                Delete Template
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>

                {filteredTemplates.length === 0 && (
                    <div className="text-center py-12">
                        <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">No email templates found</h3>
                        <p className="text-muted-foreground mb-4">
                            {searchTerm ? "No templates match your search." : "Get started by creating your first email template."}
                        </p>
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            <Dialog open={isModalOpen} onOpenChange={closeModal}>
                <DialogContent
                    className={EMAIL_TEMPLATE_EDITOR_DIALOG_CLASS}
                    overlayClassName={TEMPLATE_MODAL_OVERLAY_CLASS}
                >
                    <DialogHeader>
                        <DialogTitle>
                            {isDuplicating ? "Duplicate Template" : (isEditing ? "Edit Template" : "Create Template")}
                        </DialogTitle>
                        <DialogDescription>
                            {isDuplicating 
                                ? "Create a copy of the existing template with modifications." 
                                : (isEditing ? "Make changes to the email template." : "Create a new email template.")
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <Tabs defaultValue="content" className="w-full">
                            <TabsList className={`grid w-full grid-cols-3 ${TEMPLATE_MODAL_TABS_LIST_CLASS}`}>
                                <TabsTrigger value="content" className={TEMPLATE_MODAL_TABS_TRIGGER_CLASS}>Content</TabsTrigger>
                                <TabsTrigger value="variables" className={TEMPLATE_MODAL_TABS_TRIGGER_CLASS}>Variables</TabsTrigger>
                                <TabsTrigger value="preview" className={TEMPLATE_MODAL_TABS_TRIGGER_CLASS}>Preview</TabsTrigger>
                            </TabsList>

                            <TabsContent value="content" className="space-y-4">
                                <div className="grid gap-4">
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div className="space-y-2">
                                            <Label htmlFor="name">Template Name</Label>
                                            <Input
                                                id="name"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="e.g., Appointment Reminder"
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="key">Template Key</Label>
                                            <Input
                                                id="key"
                                                value={formData.key}
                                                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                                                placeholder="e.g., appointment-reminder"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <Label htmlFor="subject">Email Subject</Label>
                                            <VariableInsertButton
                                                onInsert={insertVariableIntoSubject}
                                                variables={insertVariables}
                                                legacy
                                            />
                                        </div>
                                        <Input
                                            id="subject"
                                            value={formData.subject}
                                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                            placeholder="e.g., Your appointment is tomorrow"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <EmailRichTextEditor
                                            id="content"
                                            label="Email Body"
                                            value={formData.content}
                                            onChange={(content) => setFormData({ ...formData, content })}
                                            variables={insertVariables}
                                            legacy
                                            placeholder="Write your email message here. Use blank lines for new paragraphs."
                                            minHeight={280}
                                        />
                                        <p className="text-sm text-muted-foreground">
                                            Format text with the toolbar, or use Insert Variable for placeholders like {`{{ $user->name }}`}.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="description">Description (optional)</Label>
                                        <Textarea
                                            id="description"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Brief note about when to use this template..."
                                            rows={2}
                                        />
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            id="is_active"
                                            checked={formData.is_active}
                                            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                        />
                                        <Label htmlFor="is_active">Active</Label>
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="variables" className="space-y-4">
                                <TemplateVariablesTab
                                    catalog={variableCatalog}
                                    customVariables={customVariables}
                                    onAddCustom={addCustomVariable}
                                    onRemoveCustom={removeCustomVariable}
                                    legacy
                                    isSaving={isSavingVariables}
                                    canPersist={isEditing && !isDuplicating}
                                />
                            </TabsContent>
                            
                            <TabsContent value="preview" className="space-y-4">
                                {livePreviewContent ? (
                                    <div className="space-y-4">
                                        <Alert>
                                            <Eye className="h-4 w-4" />
                                            <AlertDescription>
                                                Preview with sample data. Variables are replaced with example values.
                                            </AlertDescription>
                                        </Alert>
                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">Subject</Label>
                                            <div className="rounded-lg border bg-muted p-3">
                                                <p className="text-sm font-medium">{livePreviewContent.subject}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">Body</Label>
                                            <div className="email-template-preview-panel max-h-96 overflow-y-auto rounded-lg border bg-white p-4 dark:border-slate-600 dark:bg-slate-900">
                                                <div
                                                    className="email-template-preview-body"
                                                    dangerouslySetInnerHTML={{ __html: livePreviewContent.content }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="py-12 text-center">
                                        <Eye className="mx-auto h-12 w-12 text-muted-foreground" />
                                        <h3 className="mt-2 text-sm font-semibold text-muted-foreground">No content to preview</h3>
                                        <p className="mt-1 text-sm text-muted-foreground">
                                            Add a subject and body on the Content tab to see a preview here.
                                        </p>
                                    </div>
                                )}
                            </TabsContent>
                        </Tabs>
                        
                        <DialogFooter className="mt-6">
                            <Button type="button" variant="outline" onClick={closeModal}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? "Saving..." : (isDuplicating ? "Create Copy" : (isEditing ? "Update Template" : "Create Template"))}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Preview Modal */}
            <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
                <DialogContent
                    className={EMAIL_TEMPLATE_PREVIEW_DIALOG_CLASS}
                    overlayClassName={TEMPLATE_MODAL_OVERLAY_CLASS}
                >
                    <DialogHeader>
                        <DialogTitle>Email Preview</DialogTitle>
                        <DialogDescription>
                            Preview of how the email will look to recipients with sample data.
                        </DialogDescription>
                    </DialogHeader>
                    {previewContent && (
                        <div className="space-y-4">
                            <div className="p-4 bg-muted rounded-lg">
                                <div className="text-sm text-muted-foreground mb-2">Subject:</div>
                                <div className="font-semibold">{previewContent.subject}</div>
                            </div>
                            <div className="email-template-preview-panel border rounded-lg p-4 max-h-[400px] overflow-y-auto bg-white dark:border-slate-600 dark:bg-slate-900">
                                <div
                                    className="email-template-preview-body"
                                    dangerouslySetInnerHTML={{ __html: previewContent.content }}
                                />
                            </div>
                            <div className="text-xs text-muted-foreground">
                                <p><strong>Note:</strong> This preview uses sample data. Actual emails will contain real user information.</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setIsPreviewOpen(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AuthenticatedLayout>
    )
} 